import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { X, Mail, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const { login, register } = useAuth();
  const { language } = useLanguage();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const labels = {
    fr: {
      login: 'Connexion',
      register: 'Créer un compte',
      name: 'Nom complet',
      email: 'Courriel',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      submit: 'Continuer',
      noAccount: "Pas de compte?",
      hasAccount: 'Déjà un compte?',
      createAccount: "S'inscrire",
      signIn: 'Se connecter',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      invalidCredentials: 'Courriel ou mot de passe invalide',
      emailExists: 'Ce courriel est déjà utilisé',
      success: 'Connexion réussie!'
    },
    en: {
      login: 'Sign In',
      register: 'Create Account',
      name: 'Full Name',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      submit: 'Continue',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      createAccount: 'Sign Up',
      signIn: 'Sign In',
      passwordMismatch: 'Passwords do not match',
      invalidCredentials: 'Invalid email or password',
      emailExists: 'This email is already registered',
      success: 'Login successful!'
    },
    es: {
      login: 'Iniciar Sesión',
      register: 'Crear Cuenta',
      name: 'Nombre Completo',
      email: 'Correo',
      password: 'Contraseña',
      confirmPassword: 'Confirmar Contraseña',
      submit: 'Continuar',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
      createAccount: 'Registrarse',
      signIn: 'Iniciar Sesión',
      passwordMismatch: 'Las contraseñas no coinciden',
      invalidCredentials: 'Correo o contraseña inválidos',
      emailExists: 'Este correo ya está registrado',
      success: '¡Inicio de sesión exitoso!'
    },
    ar: {
      login: 'تسجيل الدخول',
      register: 'إنشاء حساب',
      name: 'الاسم الكامل',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      confirmPassword: 'تأكيد كلمة المرور',
      submit: 'متابعة',
      noAccount: 'ليس لديك حساب؟',
      hasAccount: 'لديك حساب بالفعل؟',
      createAccount: 'التسجيل',
      signIn: 'تسجيل الدخول',
      passwordMismatch: 'كلمات المرور غير متطابقة',
      invalidCredentials: 'بريد إلكتروني أو كلمة مرور غير صالحة',
      emailExists: 'هذا البريد مسجل بالفعل',
      success: 'تم تسجيل الدخول بنجاح!'
    },
    zh: {
      login: '登录',
      register: '创建账户',
      name: '全名',
      email: '邮箱',
      password: '密码',
      confirmPassword: '确认密码',
      submit: '继续',
      noAccount: '没有账户？',
      hasAccount: '已有账户？',
      createAccount: '注册',
      signIn: '登录',
      passwordMismatch: '密码不匹配',
      invalidCredentials: '邮箱或密码无效',
      emailExists: '该邮箱已注册',
      success: '登录成功！'
    }
  };

  const l = labels[language] || labels.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'register') {
        if (formData.password !== formData.confirmPassword) {
          setError(l.passwordMismatch);
          setLoading(false);
          return;
        }
        const result = await register(formData.name, formData.email, formData.password, language);
        if (result.success) {
          onClose();
        } else {
          setError(l.emailExists);
        }
      } else {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          onClose();
        } else {
          setError(l.invalidCredentials);
        }
      }
    } catch (err) {
      setError(mode === 'login' ? l.invalidCredentials : l.emailExists);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="auth-modal"
      >
        {/* Header */}
        <div className="bg-institutional-blue p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            data-testid="close-auth-modal"
          >
            <X className="h-6 w-6" />
          </button>
          <h2 className="font-serif text-2xl">
            {mode === 'login' ? l.login : l.register}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{l.name}</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-institutional-blue"
                  required
                  data-testid="auth-name"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{l.email}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-institutional-blue"
                required
                data-testid="auth-email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{l.password}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-institutional-blue"
                required
                minLength={6}
                data-testid="auth-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{l.confirmPassword}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-institutional-blue"
                  required
                  minLength={6}
                  data-testid="auth-confirm-password"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center" data-testid="auth-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-institutional-blue text-white font-medium rounded-lg hover:bg-opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="auth-submit"
          >
            {loading && <Loader2 className="h-5 w-5 animate-spin" />}
            {l.submit}
          </button>

          <div className="text-center text-sm text-gray-600">
            {mode === 'login' ? (
              <>
                {l.noAccount}{' '}
                <button
                  type="button"
                  onClick={() => { setMode('register'); setError(''); }}
                  className="text-institutional-blue font-medium hover:underline"
                  data-testid="switch-to-register"
                >
                  {l.createAccount}
                </button>
              </>
            ) : (
              <>
                {l.hasAccount}{' '}
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-institutional-blue font-medium hover:underline"
                  data-testid="switch-to-login"
                >
                  {l.signIn}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
